import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Table, Badge, Row, Col, ProgressBar, Modal, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faInfoCircle, faClock, faImage } from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';

const PortScanner = ({ projectId }) => {
  const [formData, setFormData] = useState({
    targets: '',
    ports: '80,443,8000-8100',
    timing: 'normal',
    captureScreenshots: false,
    beaconId: ''
  });
  const [timingPresets, setTimingPresets] = useState([
    { name: 'paranoid', description: 'Very slow (stealthy)', timeout: 5000, concurrency: 10, delay: 1000 },
    { name: 'normal', description: 'Balanced speed/stealth', timeout: 2000, concurrency: 50, delay: 100 },
    { name: 'fast', description: 'Quick discovery (noisy)', timeout: 1000, concurrency: 100, delay: 0 }
  ]);
  const [beacons, setBeacons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanJob, setScanJob] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBeacons();
  }, [projectId]);

  const loadBeacons = async () => {
    try {
      const response = await c2Service.getBeacons({ project: projectId });
      const beaconList = response.data?.data || [];
      setBeacons(beaconList);

      // Auto-select first online beacon
      const onlineBeacon = beaconList.find(b => b.isAlive);
      if (onlineBeacon) {
        setFormData(prev => ({ ...prev, beaconId: onlineBeacon._id }));
        // Auto-fill /24 network based on beacon's internal IP
        autoFillNetwork(onlineBeacon);
      }
    } catch (error) {
      console.error('Failed to load beacons:', error);
    }
  };

  const autoFillNetwork = (beacon) => {
    // Get beacon's internal IP and calculate /24 network
    const internalIp = beacon.systemInfo?.ipAddresses?.[0];
    if (internalIp) {
      const parts = internalIp.split('.');
      if (parts.length === 4) {
        const network = `${parts[0]}.${parts[1]}.${parts[2]}.0/24`;
        setFormData(prev => ({ ...prev, targets: network }));
      }
    }
  };

  const handleBeaconChange = (beaconId) => {
    setFormData(prev => ({ ...prev, beaconId }));
    const beacon = beacons.find(b => b._id === beaconId);
    if (beacon) {
      autoFillNetwork(beacon);
    }
  };

  const handlePortPreset = (preset) => {
    const presets = {
      'Top 100': '21,22,23,25,53,80,110,111,135,139,143,443,445,993,995,1723,3306,3389,5900,8080',
      'Top 1000': Array.from({ length: 1000 }, (_, i) => i + 1).join(','),
      'All Ports': '1-65535',
      'Web Ports': '80,443,8000,8008,8080,8443,8888,9000,9090',
      'Common Services': '21,22,23,25,53,80,110,135,139,143,443,445,1433,1521,3306,3389,5432,5900,6379,8080,27017'
    };

    if (presets[preset]) {
      setFormData(prev => ({ ...prev, ports: presets[preset] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Parse targets (one per line)
      const targets = formData.targets
        .split('\n')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      if (targets.length === 0) {
        setError('Please enter at least one target');
        setLoading(false);
        return;
      }

      const scanData = {
        targets,
        ports: formData.ports,
        timing: formData.timing,
        captureScreenshots: formData.captureScreenshots,
        projectId: projectId,
        beaconId: formData.beaconId
      };

      const response = await c2Service.createPortScan(scanData);
      const job = response.data?.data;

      setScanJob(job);
      alert(`Port scan job created!\nJob ID: ${job.jobId}\n\nView progress in the Jobs tab.`);

      // Reset form
      setFormData(prev => ({
        ...prev,
        targets: '',
        ports: '80,443,8000-8100'
      }));
    } catch (error) {
      console.error('Failed to create port scan:', error);
      setError(error.response?.data?.error || error.message || 'Failed to create port scan');
    } finally {
      setLoading(false);
    }
  };

  const getTimingDescription = (timing) => {
    const preset = timingPresets.find(p => p.name === timing);
    return preset?.description || '';
  };

  const getTimingBadge = (timing) => {
    const badges = {
      paranoid: { bg: 'secondary', icon: 'P' },
      normal: { bg: 'primary', icon: 'N' },
      fast: { bg: 'danger', icon: 'F' }
    };
    return badges[timing] || badges.normal;
  };

  return (
    <>
      <Card>
        <Card.Header style={{ backgroundColor: '#374151', borderColor: '#4b5563', color: '#fff' }}>
          <FontAwesomeIcon icon={faSearch} className="me-2" />
          Port Scanner
        </Card.Header>
        <Card.Body style={{ backgroundColor: '#1f2937' }}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#e5e7eb' }}>
                    Beacon <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.beaconId}
                    onChange={(e) => handleBeaconChange(e.target.value)}
                    required
                    style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
                  >
                    <option value="">Select beacon...</option>
                    {beacons.map(beacon => (
                      <option key={beacon._id} value={beacon._id}>
                        {beacon.systemInfo?.hostname || beacon.systemInfo?.ipAddresses?.[0] || 'Unknown'} ({beacon.systemInfo?.os || 'Unknown'}) - {beacon.isAlive ? 'Online' : 'Offline'}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text style={{ color: '#9ca3af' }}>
                    <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                    Scan will be executed from the selected beacon
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#e5e7eb' }}>
                    Targets (IPs, CIDR, hostnames - one per line) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    placeholder={`192.168.1.1\n192.168.1.0/24\nexample.com\n10.0.0.0/24`}
                    value={formData.targets}
                    onChange={(e) => setFormData({ ...formData, targets: e.target.value })}
                    required
                    style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563', fontFamily: 'monospace' }}
                  />
                  <Form.Text className="text-muted">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                    Max /24 network (254 hosts)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#e5e7eb' }}>
                    Ports <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="80,443,8000-8100"
                    value={formData.ports}
                    onChange={(e) => setFormData({ ...formData, ports: e.target.value })}
                    required
                    style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563', fontFamily: 'monospace' }}
                  />
                  <Form.Text className="text-muted">
                    Format: 80,443,8000-8100 (max 1000 ports)
                  </Form.Text>
                  <div className="mt-2">
                    <small style={{ color: '#9ca3af', marginRight: '0.5rem' }}>Quick presets:</small>
                    <ButtonGroup className="d-flex flex-wrap gap-1">
                      {['Top 100', 'Top 1000', 'Web Ports', 'Common Services'].map(preset => (
                        <Button
                          key={preset}
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handlePortPreset(preset)}
                          className="mb-1"
                          style={{ fontSize: '0.75rem', flex: '0 0 auto' }}
                        >
                          {preset}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#e5e7eb' }}>
                    <FontAwesomeIcon icon={faClock} className="me-2" />
                    Timing Preset
                  </Form.Label>
                  {timingPresets.map(preset => (
                    <div key={preset.name} className="mb-2">
                      <Form.Check
                        type="radio"
                        id={`timing-${preset.name}`}
                        name="timing"
                        label={
                          <span style={{ color: '#e5e7eb' }}>
                            <Badge bg={getTimingBadge(preset.name).bg} className="me-2">
                              {getTimingBadge(preset.name).icon} {preset.name.toUpperCase()}
                            </Badge>
                            <span style={{ fontSize: '0.875rem' }}>{preset.description}</span>
                            <br />
                            <small style={{ color: '#6b7280', marginLeft: '4.5rem' }}>
                              Timeout: {preset.timeout}ms | Concurrency: {preset.concurrency} | Delay: {preset.delay}ms
                            </small>
                          </span>
                        }
                        value={preset.name}
                        checked={formData.timing === preset.name}
                        onChange={(e) => setFormData({ ...formData, timing: e.target.value })}
                        style={{ color: '#e5e7eb' }}
                      />
                    </div>
                  ))}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="captureScreenshots"
                    label={
                      <span style={{ color: '#e5e7eb' }}>
                        <FontAwesomeIcon icon={faImage} className="me-2" />
                        Capture screenshots from open web ports
                        <br />
                        <small style={{ color: '#6b7280', marginLeft: '1.5rem' }}>
                          Automatically captures screenshots from HTTP/HTTPS services (ports 80, 443, 8000, 8080, 8443, 8888)
                        </small>
                      </span>
                    }
                    checked={formData.captureScreenshots}
                    onChange={(e) => setFormData({ ...formData, captureScreenshots: e.target.checked })}
                  />
                </Form.Group>

                <Alert variant="info" style={{ backgroundColor: '#1e3a5f', borderColor: '#3b82f6' }}>
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  <strong style={{ color: '#e5e7eb' }}>About Port Scanning</strong>
                  <ul className="mb-0 mt-2" style={{ fontSize: '0.875rem', color: '#d1d5db' }}>
                    <li>Scans run as background jobs - view progress in Jobs tab</li>
                    <li>Results are saved to the selected project</li>
                    <li>Screenshots (if enabled) appear in Screenshots tab</li>
                    <li>Use <strong>paranoid</strong> timing for stealthy scans</li>
                    <li>Use <strong>fast</strong> timing for quick discovery (noisy!)</li>
                  </ul>
                </Alert>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="primary"
                type="submit"
                disabled={loading || !formData.beaconId}
                style={{ minWidth: '120px' }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Starting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    Start Scan
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Recent Scan Result */}
      {scanJob && (
        <Card className="mt-3">
          <Card.Header style={{ backgroundColor: '#1e3a5f', borderColor: '#3b82f6', color: '#e5e7eb' }}>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            Last Scan Initiated
          </Card.Header>
          <Card.Body style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}>
            <Row>
              <Col md={6}>
                <div className="mb-2">
                  <strong>Job ID:</strong> <code>{scanJob.jobId}</code>
                </div>
                <div className="mb-2">
                  <strong>Status:</strong> <Badge bg="info">{scanJob.status}</Badge>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-2">
                  <strong>Timing:</strong> {scanJob.params?.timing}
                </div>
                <div className="mb-2">
                  <strong>Screenshots:</strong> {scanJob.params?.captureScreenshots ? 'Enabled' : 'Disabled'}
                </div>
              </Col>
            </Row>
            <Alert variant="success" className="mb-0 mt-3">
              Scan job started! View progress in the <strong>Jobs</strong> tab.
            </Alert>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default PortScanner;
